import { getLogger, httpClientInterface, requireEnvironmentConfig } from '../../utils/core';
import { type EstimateStatus, TransactionSubType } from '../transactions/transactionsTypes';

const log = getLogger('app-components/flux/invoice-actions/index.js');

export type InvoiceClientType = string; // You may want to define this more specifically if there are known values

export interface InvoiceToken {
  token: string;
  clientType?: InvoiceClientType;
  estimateStatus: EstimateStatus;
}

export type InvoiceData = {
  invoice: {
    invoiceNo: number;
    dueOn: string;
    dueAmount: number;
    originalAmount: number;
    cpAccountId?: string;
    paymentId?: string;
    paymentClientSecret?: string;
    sentOn?: string;
    postedOn?: string;
    pdf: string;
    subtype: TransactionSubType;
    estimateStatus?: EstimateStatus;
    terms?: { id: string; name: string; days: number } | null;
    invoiceDisplayOptions?: { showInvoiceTerms?: boolean } | null;
  };
  from: {
    businessName: string;
    businessEmail?: string;
    businessPhoneNo?: string;
  };
  to: {
    client: string;
    clientCompanyName?: string;
  };
};

export const getInvoicePdfBlob = async (invoiceId: string, showReceipts?: boolean): Promise<Blob> => {
  try {
    const requestUrl = `${requireEnvironmentConfig().services_url}/reports/generate-and-get-invoice`;
    const requestBody = { invoiceId, showReceipts: showReceipts ?? false };

    const response = await httpClientInterface.send('POST', requestUrl, requestBody, undefined, undefined, {
      responseType: 'blob',
    });
    log.debug('Generating invoice, response', response, response.data);

    return response.data as Blob;
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};

export const sendInvoiceEmail = async (
  invoiceId: string,
  subtype: TransactionSubType = TransactionSubType.Invoice,
  showReceipts?: boolean,
): Promise<void> => {
  try {
    let requestUrl: string;
    let requestBody: object;
    switch (subtype) {
      case TransactionSubType.InvoiceCreditMemo:
        requestUrl = `${requireEnvironmentConfig().services_url}/reports/send-credit-memo`;
        requestBody = { creditMemoId: invoiceId, showReceipts: showReceipts ?? false };
        break;
      default:
        requestUrl = `${requireEnvironmentConfig().services_url}/reports/send-invoice-email`;
        requestBody = { invoiceId, showReceipts: showReceipts ?? false };
        break;
    }

    const response = await httpClientInterface.send('POST', requestUrl, requestBody);
    log.debug('Sending email, response', response, response.data);
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};

export const viewInvoiceData = async (JWToken: string) => {
  try {
    const requestUrl = `${requireEnvironmentConfig().services_url}/reports/retrieve-invoice-info`;
    const response = (await httpClientInterface.send(
      'POST',
      requestUrl,
      { token: JWToken },
      { Authorization: null, 'qcs-dataset-id': null },
    )) as { data: InvoiceData };
    log.debug('viewInvoiceData response', response, response.data);

    return response.data;
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};

export const updateEstimateStatus = async (invoiceToken: InvoiceToken): Promise<void> => {
  try {
    const requestUrl = `${requireEnvironmentConfig().services_url}/reports/update-estimate-status`;
    const response = await httpClientInterface.send('POST', requestUrl, invoiceToken, {
      Authorization: null,
      'qcs-dataset-id': null,
    });
    log.debug('updateEstimateStatus response', response, response.data);
  } catch (error: any) {
    log.log('Received error', error, error.response);
    throw error;
  }
};
